'use client';

import { CssVarsTheme, useMediaQuery } from '@mui/material';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';
import { useMemo } from 'react';

import { getTabs } from '@/app/[subdomain]/(main)/_components/page-tabs/page-tabs.helper';
import { useMandatoryFields } from '@/app/[subdomain]/(main)/_hooks/use-mandatory-fields';

import { StyledTab, StyledTabs } from './page-tabs.styles';

interface PageTabsProps {
  isMobileInit?: boolean;
}

const PageTabs: React.FC<PageTabsProps> = ({ isMobileInit }) => {
  const params = useParams<{ subdomain: string }>();
  const basePathName = usePathname();
  const isMobile = useMediaQuery<CssVarsTheme>((theme) => theme.breakpoints.down('md'), {
    defaultMatches: isMobileInit
  });
  const { hasMandatoryFields, isFetching, isLoading } = useMandatoryFields();

  const allTabs = useMemo(() => getTabs(params.subdomain, isMobile).filter((tab) => tab), [params.subdomain, isMobile]);

  const activeValue = useMemo(() => {
    // For mobile, use the regex to get base path
    const mobileBasePath = isMobile ? basePathName.match(/^(\/[^/]+\/[^/]+)/)?.[1] : basePathName;
    // Find the matching tab path (pathname should start with the tab value)
    return allTabs.find((tab) => mobileBasePath?.startsWith(tab?.value || ''))?.value || mobileBasePath;
  }, [basePathName, isMobile, allTabs]);

  const profileValue = `/${params.subdomain}/profile`;

  const displayTabs = useMemo(() => {
    return allTabs
      .filter((tab) => tab)
      .map((tab) => {
        const isProfileTab = tab!.value === profileValue;
        const isDisabled = (hasMandatoryFields && !isProfileTab) || isFetching || isLoading;

        return (
          <StyledTab
            key={tab!.path}
            value={tab!.value}
            iconPosition='top'
            label={<span key={tab!.label}>{tab!.label}</span>}
            component={Link}
            href={tab!.path}
            disabled={isDisabled}
            icon={activeValue === tab!.value ? tab!.activeIcon : tab!.icon}
          />
        );
      });
  }, [isMobile, activeValue, allTabs, hasMandatoryFields, profileValue, basePathName, isFetching, isLoading]);

  return (
    <StyledTabs
      value={activeValue}
      className='ease-in-out animate-in slide-in-from-top'
    >
      {displayTabs}
    </StyledTabs>
  );
};

export default PageTabs;
